// Deploy-safety version watcher for the agent app (DSAF-01, DSAF-02, DSAF-04).
//
// Owns a module-scope store (readable through useVersionStatus) and the
// triggers that keep it fresh. It renders nothing; the banner and overlay
// components read the store.
//
// TRIGGERS: mount, visibilitychange to visible, window focus, pageshow, online,
// route change, and a 4-minute interval. Visibility events, not timers, are the
// contract: a hidden tab has its timers throttled or frozen, so the first
// visible event must be able to decide on its own. Every trigger therefore
// re-evaluates SYNCHRONOUSLY against what the tab already knows (a tab that
// wakes past the 30-minute window shows the overlay before any round trip,
// D-03) and only then starts the network refresh. The refresh is skipped
// inside MIN_FETCH_GAP_MS unless forced, so focus plus visibility firing
// together costs one request.
//
// MINIMUM: appConfig/clientVersion is read with a single getDoc, and only when
// a user is signed in (pre-auth routes would only collect permission-denied
// noise). D-12 is a polling decision: there is NO live listener for the version or
// the minimum, because a standing listener per open tab costs more than a
// 4-minute poll buys.
//
// A raised minimum above the local build, or a CLIENT_VERSION_TOO_OLD rejection
// announced by appCallable, puts the tab in status forced at once (D-11, D-13).
// The server flavor is sticky until the page reloads.
//
// LOCKOUT: when the live build itself sits below the minimum, a reload would
// only load the same refused code, so the status is lockout instead of forced:
// no reload, and the overlay says an update is being fixed. The poll keeps
// running, so the tab moves to forced (and reloads) once a fixed build is live.
//
// LATCH: once a tab reaches overlay or forced, a later check never drops it back
// to banner (a second deploy mid-countdown would otherwise restart 30 minutes
// and unmount the overlay). Only current, lockout, or the other blocking status
// replaces it.
import React from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import buildInfo from '../buildInfo.json';
import { onClientTooOld } from '../utils/appCallable';
import {
    POLL_INTERVAL_MS,
    MIN_FETCH_GAP_MS,
    localBuildFrom,
    parseVersionManifest,
    parseMinBuild,
    computeSkewMs,
    evaluate,
    firstSeenKey,
} from '../utils/versionWatch';

const INITIAL_STATE = Object.freeze({
    status: 'current',
    deadline: null,
    remote: null,
    minBuild: 0,
    firstDetectedAt: null,
    skewMs: 0,
    forcedReason: null,
});

const defaultDeps = () => ({
    fetchImpl: (...args) => window.fetch(...args),
    readMinBuild: async () => {
        const snap = await getDoc(doc(db, 'appConfig', 'clientVersion'));
        const data = snap && typeof snap.data === 'function' ? snap.data() : null;
        return parseMinBuild(data);
    },
    getUser: () => auth.currentUser,
    storage: (() => {
        try {
            return window.sessionStorage;
        } catch {
            return null;
        }
    })(),
    now: () => Date.now(),
});

let deps = null;
const getDeps = () => {
    if (!deps) deps = defaultDeps();
    return deps;
};

let state = INITIAL_STATE;
const listeners = new Set();
let lastFetchAt = null;
let deadlineTimer = null;
let tooOldUnsub = null;
let watchers = 0;

const localBuild = () => localBuildFrom(buildInfo);

export function getVersionState() {
    return state;
}

export function subscribeVersionState(listener) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

export function nowCorrected() {
    return getDeps().now() + state.skewMs;
}

function breadcrumb(prev, next) {
    if (prev.status === next.status) return;
    if (next.status !== 'overlay' && next.status !== 'forced') return;
    try {
        if (typeof Sentry.addBreadcrumb === 'function') {
            Sentry.addBreadcrumb({
                category: 'deploy-safety',
                level: 'info',
                message: `version status ${prev.status} -> ${next.status}`,
                data: { forcedReason: next.forcedReason },
            });
        }
    } catch {
        // telemetry must never break the watcher
    }
}

function setState(patch) {
    const prev = state;
    const next = { ...prev, ...patch };
    const changed = Object.keys(next).some((key) => next[key] !== prev[key]);
    if (!changed) return;
    state = next;
    breadcrumb(prev, next);
    scheduleDeadline();
    listeners.forEach((listener) => listener());
}

function evaluateNow() {
    return evaluate({
        localBuild: localBuild(),
        remote: state.remote,
        minBuild: state.minBuild,
        firstDetectedAt: state.firstDetectedAt,
        now: nowCorrected(),
    });
}

const BLOCKING = new Set(['overlay', 'forced']);

function reevaluate() {
    const result = evaluateNow();
    // A server rejection is sticky until the page reloads, unless the live build
    // turns out to be below the minimum (reloading would loop).
    if (state.forcedReason === 'server' && result.status !== 'lockout') return;
    if (BLOCKING.has(state.status) && result.status === 'banner') return;
    const forcedReason = result.status === 'forced' ? 'minimum' : null;
    setState({ status: result.status, deadline: result.deadline, forcedReason });
}

function clearDeadlineTimer() {
    if (deadlineTimer !== null) {
        clearTimeout(deadlineTimer);
        deadlineTimer = null;
    }
}

// While a banner is showing in a visible tab, wake exactly at the deadline so
// the overlay appears without waiting for the next poll.
function scheduleDeadline() {
    clearDeadlineTimer();
    if (watchers === 0) return;
    if (state.status !== 'banner' || state.deadline === null) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const delay = Math.max(0, state.deadline - nowCorrected());
    deadlineTimer = setTimeout(() => {
        deadlineTimer = null;
        reevaluate();
        if (state.status === 'banner') scheduleDeadline();
    }, delay);
}

export async function fetchLiveManifest({ fetchImpl } = {}) {
    const d = getDeps();
    const doFetch = fetchImpl || d.fetchImpl;
    try {
        const sentAt = d.now();
        const res = await doFetch(`/version.json?t=${sentAt}`, { cache: 'no-store' });
        if (!res || !res.ok) return null;
        const type = res.headers && res.headers.get ? res.headers.get('content-type') : '';
        if (!type || !type.toLowerCase().includes('json')) return null;
        const manifest = parseVersionManifest(await res.json());
        if (!manifest) return null;
        const skewMs = computeSkewMs(res.headers.get('date'), d.now());
        return { manifest, skewMs };
    } catch {
        return null;
    }
}

function readFirstSeen(storage, builtAt) {
    try {
        const raw = storage && storage.getItem(firstSeenKey(builtAt));
        const n = raw === null || raw === undefined ? NaN : Number(raw);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
}

const FIRST_SEEN_PREFIX = firstSeenKey('');

// Only the build being counted down needs its firstSeen key, so the keys of
// earlier builds are dropped when a new one is written.
function dropOtherFirstSeen(storage, keep) {
    if (!storage || typeof storage.key !== 'function' || typeof storage.removeItem !== 'function') return;
    const stale = [];
    for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key && key !== keep && key.startsWith(FIRST_SEEN_PREFIX)) stale.push(key);
    }
    stale.forEach((key) => storage.removeItem(key));
}

function writeFirstSeen(storage, builtAt, at) {
    try {
        if (!storage) return;
        storage.setItem(firstSeenKey(builtAt), String(at));
        dropOtherFirstSeen(storage, firstSeenKey(builtAt));
    } catch {
        // storage unavailable; the clock falls back to the in-memory value
    }
}

async function refreshManifest() {
    const d = getDeps();
    const live = await fetchLiveManifest({});
    if (!live) return;
    const { manifest, skewMs } = live;
    let firstDetectedAt = state.firstDetectedAt;
    if (manifest.builtAt !== localBuild()) {
        const at = d.now() + skewMs;
        const stored = readFirstSeen(d.storage, manifest.builtAt);
        if (stored !== null) {
            firstDetectedAt = stored;
        } else if (firstDetectedAt === null || state.remote?.builtAt !== manifest.builtAt) {
            firstDetectedAt = at;
            writeFirstSeen(d.storage, manifest.builtAt, at);
        }
    } else {
        firstDetectedAt = null;
    }
    setState({ remote: manifest, skewMs, firstDetectedAt });
    reevaluate();
}

async function refreshMinimum() {
    const d = getDeps();
    let user = null;
    try {
        user = d.getUser();
    } catch {
        user = null;
    }
    if (!user) return;
    try {
        const minBuild = await d.readMinBuild();
        if (Number.isFinite(minBuild) && minBuild !== state.minBuild) {
            setState({ minBuild });
        }
        reevaluate();
    } catch {
        // permission-denied or offline: the next check tries again
    }
}

export async function checkVersionNow({ force = false } = {}) {
    reevaluate();
    const at = getDeps().now();
    if (!force && lastFetchAt !== null && at - lastFetchAt < MIN_FETCH_GAP_MS) return;
    lastFetchAt = at;
    await Promise.all([refreshManifest(), refreshMinimum()]);
}

function ensureTooOldSubscription() {
    if (tooOldUnsub) return;
    tooOldUnsub = onClientTooOld(() => {
        // Already known to be a lockout: a forced reload would loop on the same build.
        if (evaluateNow().status === 'lockout') {
            setState({ status: 'lockout', deadline: null, forcedReason: null });
            return;
        }
        setState({ status: 'forced', deadline: nowCorrected(), forcedReason: 'server' });
    });
}

export function __resetVersionWatchForTests() {
    clearDeadlineTimer();
    if (tooOldUnsub) {
        tooOldUnsub();
        tooOldUnsub = null;
    }
    state = INITIAL_STATE;
    lastFetchAt = null;
    watchers = 0;
    deps = null;
    listeners.clear();
}

export function __setVersionWatchDepsForTests(overrides) {
    deps = { ...defaultDeps(), ...overrides };
}

const subscribe = (listener) => subscribeVersionState(listener);
const getServerState = () => INITIAL_STATE;

export function useVersionStatus() {
    return React.useSyncExternalStore(subscribe, getVersionState, getServerState);
}

export function useVersionWatch({ enabled = true } = {}) {
    const snapshot = React.useSyncExternalStore(subscribe, getVersionState, getServerState);
    const location = useLocation();

    React.useEffect(() => {
        if (!enabled) return undefined;
        watchers += 1;
        ensureTooOldSubscription();

        const check = () => { checkVersionNow(); };
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                check();
                scheduleDeadline();
            } else {
                clearDeadlineTimer();
            }
        };
        const onPageShow = (event) => {
            if (event && event.persisted === false) return;
            check();
        };

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', check);
        window.addEventListener('pageshow', onPageShow);
        window.addEventListener('online', check);
        const interval = setInterval(check, POLL_INTERVAL_MS);
        scheduleDeadline();
        check();

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', check);
            window.removeEventListener('pageshow', onPageShow);
            window.removeEventListener('online', check);
            clearInterval(interval);
            watchers = Math.max(0, watchers - 1);
            if (watchers === 0) {
                clearDeadlineTimer();
                if (tooOldUnsub) {
                    tooOldUnsub();
                    tooOldUnsub = null;
                }
            }
        };
    }, [enabled]);

    // A route change re-checks; the 30 s gap in checkVersionNow bounds the cost.
    React.useEffect(() => {
        if (!enabled) return;
        checkVersionNow();
    }, [enabled, location.key, location.pathname]);

    return snapshot;
}
